import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { isEmbedded } from '@/lib/embed';

import styles from './Home.module.css';
import shared from '@/pages/pages.module.css';
import { useT, tr, type MessageKey } from '@/i18n';
import type { Kind } from '@/services/createBridge';

import { AppIcon } from './AppIcons';
import { ArrowUpRightIcon, ChevronRightIcon } from './Icons';
import type { HomeTilesProps } from './HomeTiles.types';
import { NearbyBadge } from './Nearby';


/** The choices of what to send; the receive plate below them has its own place. */
const SEND_TILES: readonly { readonly id: Exclude<Kind, 'receive'>; readonly title: MessageKey; readonly text: MessageKey; readonly more?: MessageKey }[] = [
  { id: 'password', title: 'homeTiles.passwordTitle', text: 'homeTiles.passwordText', more: 'homeTiles.passwordMore' },
  { id: 'chat', title: 'homeTiles.chatTitle', text: 'homeTiles.chatText' },
];

/** The first screen: what would you like to send, and the way round to receive here. */
export function HomeTiles({ cryptoOk, dragging, dragProps, onChoose }: HomeTilesProps): React.JSX.Element {
  const t = useT();
  const rest = { rest: (children: ReactNode) => <span className={styles.receiveRest}>{children}</span> };
  const phoneBreak = { br: () => <br className={shared.phoneBreak} /> };
  return (
    <div className={shared.tiles} {...dragProps}>
      <h2 className={styles.tilesTitle}>
        {tr('homeTiles.title', phoneBreak)}
      </h2>
      <p className={styles.tilesLead}>
        {tr('homeTiles.lead', { ...phoneBreak, nowrap: (children) => <span className={styles.nowrap}>{children}</span> })}
      </p>
      <div className={[shared.tileGrid, dragging ? shared.tileGridDrop : ''].join(' ')}>
        {SEND_TILES.map((item) => (
          <button key={item.id} type="button" className={shared.tile} onClick={() => { onChoose(item.id); }} data-testid={`tile-${item.id}`} disabled={!cryptoOk}>
            <AppIcon kind={item.id} className={shared.tileIcon} />
            <span className={styles.tileBody}>
              <span className={styles.tileTitle}>{t(item.title)}</span>
              <span className={styles.tileText}>
                {t(item.text)}
                {/* a wide tile puts the second sentence on a line of its own */}
                {item.more === undefined ? null : <span className={styles.tileTextMore}> {t(item.more)}</span>}
              </span>
            </span>
            <ChevronRightIcon size={18} className={shared.tileChevron} />
            {/* on a wide screen: an arrow in the corner, level with the icon, that leans on under the pointer */}
            <ArrowUpRightIcon size={20} className={shared.tileArrow} />
          </button>
        ))}
      </div>
      <p className={styles.receiveLabel}>{t('homeTiles.receiveLabel')}</p>
      {/* The other way round: one plate to receive here; under it, how this device is found nearby and the way to type a code */}
      <div className={shared.receiveGroup}>
        <button type="button" className={shared.receivePlate} onClick={() => { onChoose('receive'); }} data-testid="tile-receive" disabled={!cryptoOk}>
          <AppIcon kind="receive" className={shared.receiveIcon} />
          <span className={styles.tileBody}>
            {/* the phone app says it shorter: “Receive”, “Show a QR code or a link” */}
            <span className={styles.receiveTitle}>
              {tr('homeTiles.receiveTitle', rest)}
            </span>
            <span className={styles.receiveText}>
              {tr('homeTiles.receiveText', rest)}
            </span>
          </span>
          <ChevronRightIcon size={18} className={shared.receiveChevron} />
          <ArrowUpRightIcon size={20} className={shared.receiveArrow} />
        </button>
      </div>
      <div className={shared.receiveFoot}>
        <NearbyBadge className={styles.nearbyLine} reserve={cryptoOk} />
        {isEmbedded() ? null : (
          <Link to="/connect" className={shared.codeLink} data-testid="go-connect">
            {tr('homeTiles.haveCode', { strong: (children) => <span className={shared.codeLinkStrong}>{children}</span> })}
            <ChevronRightIcon size={14} />
          </Link>
        )}
      </div>
      {!cryptoOk ? <p className={[shared.notice, shared.noticeWarn].join(' ')}>{t('homeTiles.noCrypto')}</p> : null}
    </div>
  );
}
